// Invoice records — one doc per assigned guard per job. Created from the
// Review modal when the owner approves billing. Fed into the Invoices page.
//
// Doc shape (collection: "invoices"):
//   invoiceNumber:  "INV-2026-0142"   sequential, company-scoped
//   companyId:      string
//   jobId:          string
//   jobNumber:      string
//   brokerId:       string
//   brokerName:     string
//   guardNumber:    1..4
//   guardName:      string
//   isDriver:       boolean (drivers carry the mileage on this invoice)
//   jobType:        outbound|inbound|direct
//   airportCode:    string
//   completedAt:    Timestamp/ms
//   hoursCharged:   number
//   miles:          number   (0 for non-driver invoices)
//   expenses:       number   full job expenses on guard #1's invoice; 0 on the rest
//   total:          number   per-guard line total (hours+miles, +expenses on G1)
//   status:         draft|sent|paid|void
//   payCycle:       "YYYY-MM-A" | "YYYY-MM-B"  per Orion FAQ
//   createdAt, sentAt, paidAt, voidedAt: Timestamp
//
// Pay cycles (Orion FAQ):
//   A = 2nd–15th    (cutoff midnight central on the 15th, checks 22nd)
//   B = 16th–1st    (cutoff midnight central on the 1st, checks 8th of next month)
// We bucket by completedAt as a default; the owner can override on the invoice.

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  runTransaction,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { guardOwnsCar, ownCarCount } from "./billing";

// ── Pay-cycle helpers ────────────────────────────────────────────────

function tsToDate(ts) {
  if (!ts) return null;
  if (typeof ts === "number") return new Date(ts);
  if (typeof ts === "string") {
    const ms = Date.parse(ts);
    return Number.isNaN(ms) ? null : new Date(ms);
  }
  if (ts.toMillis) return new Date(ts.toMillis());
  if (ts.seconds != null) return new Date(ts.seconds * 1000);
  return null;
}

/**
 * Bucket a date into a pay cycle ID.
 *   day 1–15  → "{YYYY}-{MM}-A"   (this month's first cycle)
 *   day 16–31 → "{YYYY}-{MM}-B"   (closes on 1st of next month)
 * Day 1 is technically the close of the previous month's B-cycle, but in
 * practice anything *completed* on the 1st belongs to the new month's A.
 */
// ── Dispatcher billing cycles ───────────────────────────────────────
// Different from guard payCycle. This is "when do we bill the dispatcher?"
//   Orion       → twice/month  (1–15 and 16–end)
//   Ferrari     → monthly      (full calendar month)
//   Executive   → monthly      (full calendar month)
//   (anything else) → monthly fallback
//
// Returns { id, label, startMs, endMs }.
// id format: "ORION-2026-05-A" or "FERRARI-2026-05".
export function dispatcherCycleFor(dispatcherName, date) {
  const d = date instanceof Date ? date : (typeof date === "number" ? new Date(date) : new Date());
  const y = d.getFullYear();
  const m = d.getMonth(); // 0-indexed
  const day = d.getDate();
  const name = String(dispatcherName || "").toLowerCase();
  const isHalfMonth = name.includes("orion");

  if (isHalfMonth) {
    const half = day <= 15 ? "A" : "B";
    const startDay = half === "A" ? 1 : 16;
    const start = new Date(y, m, startDay, 0, 0, 0, 0);
    // End-of-cycle: day 15 23:59:59 for A, last day of month 23:59:59 for B.
    const end = half === "A"
      ? new Date(y, m, 15, 23, 59, 59, 999)
      : new Date(y, m + 1, 0, 23, 59, 59, 999);
    const mm = String(m + 1).padStart(2, "0");
    return {
      id: `${dispatcherName || "ORION"}-${y}-${mm}-${half}`,
      label: `${monthShort(m)} ${startDay}–${half === "A" ? 15 : end.getDate()}, ${y}`,
      startMs: start.getTime(),
      endMs: end.getTime(),
      kind: "half-month",
      dispatcherName: dispatcherName || "Orion",
    };
  }

  // Monthly fallback (Ferrari, Executive, anything else).
  const start = new Date(y, m, 1, 0, 0, 0, 0);
  const end = new Date(y, m + 1, 0, 23, 59, 59, 999);
  const mm = String(m + 1).padStart(2, "0");
  return {
    id: `${dispatcherName || "MONTHLY"}-${y}-${mm}`,
    label: `${monthShort(m)} ${y}`,
    startMs: start.getTime(),
    endMs: end.getTime(),
    kind: "monthly",
    dispatcherName: dispatcherName || "",
  };
}

// Step a dispatcher cycle backward (n=-1) or forward (n=+1). Used by
// the ◀ ▶ buttons on the summary card. Half-month cycles alternate
// A↔B inside a month, then roll to the adjacent month — naive day
// arithmetic skipped halves, so we navigate explicitly by half.
export function shiftDispatcherCycle(cycle, n) {
  if (!cycle) return cycle;
  if (cycle.kind === "monthly") {
    const ref = new Date(cycle.startMs);
    ref.setMonth(ref.getMonth() + n);
    return dispatcherCycleFor(cycle.dispatcherName, ref);
  }
  // half-month — startMs's date is either 1 (cycle A) or 16 (cycle B).
  const ref = new Date(cycle.startMs);
  const day = ref.getDate();
  if (n > 0) {
    // Forward: A (day 1) → B same month; B (day 16) → A next month.
    if (day === 1) ref.setDate(16);
    else { ref.setMonth(ref.getMonth() + 1); ref.setDate(1); }
  } else {
    // Backward: B (day 16) → A same month; A (day 1) → B prev month.
    if (day >= 16) ref.setDate(1);
    else { ref.setMonth(ref.getMonth() - 1); ref.setDate(16); }
  }
  return dispatcherCycleFor(cycle.dispatcherName, ref);
}

// Infer which top-level dispatcher an invoice belongs to. Always
// returns one of "Orion", "Ferrari", or "Executive" — the three
// entities we actually bill. Checks the explicit dispatcherName field
// first (which may be more specific, e.g. "Ferrari Express Inc." or
// "Orion - Bill Schmitt"), then falls back to broker-name inference
// for older invoices that don't have dispatcherName saved.
export function inferInvoiceDispatcher(inv) {
  const explicit = String(inv?.dispatcherName || "").toLowerCase();
  if (explicit.includes("ferrari")) return "Ferrari";
  if (explicit.includes("executive")) return "Executive";
  if (explicit.includes("orion")) return "Orion";
  // No usable dispatcherName — fall back to broker.
  const broker = String(inv?.brokerName || "").toLowerCase();
  if (broker.includes("ferrari")) return "Ferrari";
  if (broker.includes("executive")) return "Executive";
  return "Orion";
}

function monthShort(monthIdx) {
  return ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][monthIdx];
}

/**
 * Package detail rows for an invoice — one per package on the job:
 * the Order/Receipt/HAWB number plus the package's stop name + address.
 * Falls back to the job's main non-airport stop (outbound: first pickup;
 * inbound/direct: last stop) when a package isn't bound to a stop, and
 * synthesizes a single address-only row for jobs with no packages, so
 * the Statements view always has an address to show. Denormalized onto
 * the invoice doc at create time (and backfilled for older invoices).
 */
export function packageDetailsFor(job) {
  const stops = Array.isArray(job?.stops) ? job.stops : [];
  const real = stops.filter((s) => s.type !== "airport");
  const fallbackStop = job?.jobType === "outbound" ? real[0] : real[real.length - 1];
  const pkgs = Array.isArray(job?.packages) && job.packages.length ? job.packages : [null];
  return pkgs.map((pkg) => {
    const stop = (pkg?.stopId && stops.find((s) => s.id === pkg.stopId)) || fallbackStop || null;
    return {
      number: pkg?.hawbNumber || "",
      locationName: stop?.locationName || "",
      address: stop?.address || "",
    };
  });
}

export function payCycleFor(date) {
  const d = date instanceof Date ? date : tsToDate(date) || new Date();
  // Cycles run 2nd–15th (A) and 16th–1st of the NEXT month (B) — see
  // payCycleLabel. So the 1st belongs to the PREVIOUS month's B half;
  // the old `<= 15 → A` bucketing put 1st-of-month completions a cycle
  // early (an Aug 1 job landed in "Aug 2 – 15" instead of "Jul 16 – Aug 1").
  if (d.getDate() === 1) {
    const prevY = d.getMonth() === 0 ? d.getFullYear() - 1 : d.getFullYear();
    const prevM = String(d.getMonth() === 0 ? 12 : d.getMonth()).padStart(2, "0");
    return `${prevY}-${prevM}-B`;
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const half = d.getDate() <= 15 ? "A" : "B";
  return `${y}-${m}-${half}`;
}

/** Friendly label: "Apr 16 – May 1, 2026" */
export function payCycleLabel(cycle) {
  if (!cycle) return "";
  const m = /^(\d{4})-(\d{2})-([AB])$/.exec(cycle);
  if (!m) return cycle;
  const [, y, mm, half] = m;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const monthName = months[Number(mm) - 1];
  if (half === "A") return `${monthName} 2 – 15, ${y}`;
  // B closes on the 1st of the *next* month
  const nextMonth = months[Number(mm) % 12];
  const nextY = Number(mm) === 12 ? Number(y) + 1 : Number(y);
  return `${monthName} 16 – ${nextMonth} 1, ${nextY}`;
}

// ── Invoice numbering (per company, sequential) ──────────────────────
// Counter lives at companies/{companyId}.invoiceCounter (integer).
// We bump it inside a transaction so concurrent approvals don't collide.

async function nextInvoiceNumber(companyId) {
  const ref = doc(db, "companies", companyId);
  const next = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const cur = snap.exists() ? Number(snap.data().invoiceCounter) || 0 : 0;
    const n = cur + 1;
    tx.update(ref, { invoiceCounter: n });
    return n;
  });
  const year = new Date().getFullYear();
  return `INV-${year}-${String(next).padStart(4, "0")}`;
}

// ── Create invoices for a completed job ──────────────────────────────

/**
 * Create one invoice per assigned guard for `job`, using the live billing
 * snapshot. Returns the array of created invoice IDs and their numbers.
 *
 * Per-guard math:
 *   hourly $ = hourlyRate × hoursCharged                     (every guard)
 *   miles $  = mileageRate × miles    if isDriver else 0     (drivers only)
 *   expenses = full amount on guard #1 only (one charge to the broker,
 *              not smeared across every guard's activity log)
 *   total    = hourly + miles (+ expenses on G1)
 */
/**
 * Delete any draft invoices already on file for a given job. Called by
 * createInvoicesForJob before it writes new ones — otherwise re-approving
 * the same job (e.g. the owner bumped numGuards from 1 to 2 and clicked
 * Approve again) leaves the old per-guard invoices orphaned with stale
 * hours/miles next to the fresh ones. Only drafts are deleted; anything
 * in sent/paid/void is left alone (deleting a sent invoice would break
 * reconciliation with the broker).
 *
 * Returns how many drafts were removed, for the audit log / toast.
 */
export async function deleteDraftInvoicesForJob(companyId, jobId) {
  if (!companyId || !jobId) return 0;
  const q = query(
    collection(db, "invoices"),
    where("companyId", "==", companyId),
    where("jobId", "==", jobId),
  );
  const snap = await getDocs(q);
  let removed = 0;
  for (const d of snap.docs) {
    if (d.data().status === "draft") {
      // eslint-disable-next-line no-await-in-loop
      await deleteDoc(d.ref);
      removed++;
    }
  }
  return removed;
}

// Any sent/paid invoices on file for this job? The Review modal checks
// this before a re-approve (e.g. the owner bumped the guard count after
// approving): draft invoices are safely wiped and recreated, but a
// sent/paid one must be voided first or the re-approve would create a
// duplicate bill alongside it.
export async function hasSentInvoicesForJob(companyId, jobId) {
  if (!companyId || !jobId) return false;
  const snap = await getDocs(query(
    collection(db, "invoices"),
    where("companyId", "==", companyId),
    where("jobId", "==", jobId),
  ));
  return snap.docs.some((d) => ["sent", "paid"].includes(d.data().status));
}

/**
 * Orion bills on the 1st and the 15th. A job approved AFTER its own
 * half-month cycle has closed can't ride on that (already-submitted)
 * statement — it belongs on the next one, regardless of the job's date.
 * Returns the billingCycleDate (YYYY-MM-DD, the target cycle's start)
 * to stamp on the invoice when that's the case, else null (the job's
 * natural cycle is still open, or the payer isn't half-month Orion).
 * The owner can still move it afterwards; this only sets the default.
 *
 * Pure so it's unit-testable — `now` is injected.
 */
export function lateApprovalCycleDate(dispatcherName, scheduledDate, now = new Date()) {
  if (!scheduledDate) return null;
  const natural = dispatcherCycleFor(dispatcherName, (() => {
    const [y, m, d] = String(scheduledDate).split("-").map(Number);
    return y && m && d ? new Date(y, m - 1, d, 12, 0) : null;
  })());
  if (!natural || natural.kind !== "half-month") return null;
  const nowMs = now instanceof Date ? now.getTime() : Number(now) || Date.now();
  if (nowMs <= natural.endMs) return null; // natural cycle still open
  // Cycle containing "now" is the next open statement.
  const target = dispatcherCycleFor(dispatcherName, new Date(nowMs));
  const s = new Date(target.startMs);
  return `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, "0")}-${String(s.getDate()).padStart(2, "0")}`;
}

export async function createInvoicesForJob(job, broker, billing) {
  if (!job?.companyId) throw new Error("Job is missing companyId");

  // Wipe any prior drafts for THIS job first so re-approving doesn't
  // pile up duplicate per-guard rows with stale numbers. Safe: drafts
  // haven't been sent to the broker yet.
  if (job.id) {
    try { await deleteDraftInvoicesForJob(job.companyId, job.id); }
    catch (err) { console.warn("Couldn't clear stale drafts before re-approve", err); }
  }

  const numGuards = Math.max(1, Number(job.numGuards) || 1);
  // Mileage sets = own-car guards (each car bills mileage). Default toggles
  // (G1/G3 drive) match the old mileageSetsFor() result, so legacy jobs are
  // unchanged; an own-car passenger now adds a billed car.
  const mileageSets = ownCarCount(job);
  const assignments = Array.isArray(job.guardAssignments) ? job.guardAssignments : [];

  const hourlyRate = Number(broker?.hourlyRate) || 0;
  const mileageRate = Number(broker?.mileageRate) || 0;
  const hoursCharged = Number(billing?.hoursCharged) || 0;
  const miles = Number(billing?.miles) || 0;
  const totalExpenses = Number(billing?.expenses) || 0;
  // Expenses (vault fees, tolls, …) are ONE charge to the broker — they
  // ride entirely on guard #1's invoice/activity log instead of being
  // smeared as fractional shares across every guard's paperwork. The
  // statement total is unchanged (the old even split summed to the same
  // amount, minus penny drift on 3-guard jobs).
  const expensesFor = (guardNumber) => (guardNumber === 1 ? totalExpenses : 0);
  const completedAt = job.completedAt || job.completedAtMs || Date.now();
  const cycle = payCycleFor(completedAt);

  // Flat-fee detection. DAI (and any flat-rate broker) bills a fixed
  // amount per job, NOT hourly+mileage. computeJobBilling exposes the
  // resolved per-guard flat fee (job.flatFeeOverride > broker.flatFee).
  // Without this, a DAI invoice came out $0 because hours+miles are 0,
  // which silently zeroed out the whole statement total.
  const flatFeePerGuard = Number(billing?.flatFee) || 0;
  const useFlat = flatFeePerGuard > 0;

  // Per-guard hours support. When a guard splits off early from a multi-
  // guard job (e.g. one returns to the airport before the rest finish a
  // far-flung delivery), their hours are shorter than the route end.
  // guardAssignments[i].endTime is the override; if blank, the guard
  // bills at the job-level hoursCharged (full route, current behavior).
  // Computed: (endTime − pickupStartTime), floored at the broker minimum.
  const minHours = Number(broker?.minHours) || 0;
  function timeToMin(s) {
    if (!s || !/^\d{1,2}:\d{2}$/.test(s)) return null;
    const [h, m] = String(s).split(":").map(Number);
    return h * 60 + m;
  }
  function hoursForGuard(guardNumber) {
    const a = assignments.find((g) => g.guardNumber === guardNumber);
    // The review panel already resolved this guard's day — a typed override,
    // a direct hours entry, or a computed default like the relieved-at-the-
    // airport end. Trust it, so the invoice can't disagree with the screen
    // the owner approved.
    if (Number.isFinite(Number(a?.billedHours)) && Number(a.billedHours) > 0) {
      return Math.max(Number(a.billedHours), minHours);
    }
    const et = a?.billedEndTime || a?.endTime;
    if (!et) return hoursCharged; // no per-guard override → job-level
    const startMin = timeToMin(a?.billedStartTime || job.pickupStartTime);
    let endMin = timeToMin(et);
    if (startMin == null || endMin == null) {
      return hoursCharged; // unparseable → fall back
    }
    // Next-day wrap: a guard who clocks out at 00:21 after a 10:30 start
    // worked 13.85h, not negative. Mirrors the ReadyToBill preview.
    if (endMin <= startMin) endMin += 24 * 60;
    const raw = (endMin - startMin) / 60;
    return Math.max(raw, minHours);
  }

  // Denormalize a route summary onto the invoice so the Statements view
  // can show the same "Pickup → Delivery" line the Ready-to-Bill list
  // does, without round-tripping to load the job's stops. Non-airport
  // stops only; OOT stops render as "OUT OF TOWN".
  const routeSummary = (() => {
    const stops = Array.isArray(job.stops) ? job.stops : [];
    const real = stops.filter((s) => s.type !== "airport");
    if (real.length === 0) return "";
    return real
      .map((s) => (s.outOfTown ? "OUT OF TOWN" : (s.locationName || "—")))
      .join(" → ");
  })();

  const created = [];
  for (let n = 1; n <= numGuards; n++) {
    const a = assignments.find((g) => g.guardNumber === n);
    const isDriver = guardOwnsCar(a, n); // own car → bills mileage
    // Per-guard miles: a car that covered fewer stops has a stored billedMiles
    // (set from the per-stop coverage in the review); else it ran the full
    // route. Passengers bill 0.
    const guardMilesN = isDriver ? (a?.billedMiles != null ? Number(a.billedMiles) : miles) : 0;

    const guardExpenses = expensesFor(n);
    let hourly$, miles$, total;
    let thisGuardHours = hoursCharged;
    if (useFlat) {
      // Flat-rate: every guard's slice is the flat fee (+ expenses on
      // G1 only). Hours/miles don't factor in. Record 0s for them so
      // the workbook + audit trail are honest.
      hourly$ = 0;
      miles$ = 0;
      total = Math.round((flatFeePerGuard + guardExpenses) * 100) / 100;
    } else {
      thisGuardHours = hoursForGuard(n);
      hourly$ = Math.round(hourlyRate * thisGuardHours * 100) / 100;
      miles$ = Math.round(mileageRate * guardMilesN * 100) / 100;
      total = Math.round((hourly$ + miles$ + guardExpenses) * 100) / 100;
    }

    // eslint-disable-next-line no-await-in-loop
    const invoiceNumber = await nextInvoiceNumber(job.companyId);

    // Dispatcher (Orion / Ferrari / Executive) — drives the per-
    // dispatcher cycle bucketing in the summary view. The broker's
    // RATE CARD is the source of truth (the job's snapshot can be
    // stale — e.g. Kallati jobs created with "Orion" would bill into
    // the wrong cycle). For older invoices missing this field,
    // inferInvoiceDispatcher falls back to broker-name inference.
    const invoiceDispatcher = broker?.dispatcherName || job.dispatcherName || (() => {
      const b = String(job.brokerName || broker?.name || "").toLowerCase();
      if (b.includes("ferrari")) return "Ferrari";
      if (b.includes("executive")) return "Executive";
      return "Orion";
    })();
    // A cycle the owner already moved this job to (re-approve) is kept;
    // otherwise apply the Orion late-approval default.
    const lateCycleDate = job.billingCycleDate
      || lateApprovalCycleDate(invoiceDispatcher, job.scheduledDate);

    const docData = {
      invoiceNumber,
      companyId: job.companyId,
      jobId: job.id || null,
      jobNumber: job.jobNumber || "",
      brokerId: job.brokerId || null,
      brokerName: job.brokerName || broker?.name || "",
      // Dispatcher (Orion / Ferrari / Executive) — drives the per-
      // dispatcher cycle bucketing in the summary view. The broker's
      // RATE CARD is the source of truth (the job's snapshot can be
      // stale — e.g. Kallati jobs created with "Orion" would bill into
      // the wrong cycle). For older invoices missing this field,
      // inferInvoiceDispatcher falls back to broker-name inference.
      dispatcherName: invoiceDispatcher,
      // Orion late approval: the job's own 1st/15th statement has already
      // closed by the time it's approved → default it onto the NEXT
      // statement (billingCycleDate wins over scheduledDate for
      // statement grouping only; the job's real date is untouched). The
      // owner can move it from there. Null when the natural cycle is
      // still open, or a prior move already stamped a cycle on the job.
      ...(lateCycleDate ? { billingCycleDate: lateCycleDate } : {}),
      // Scheduled date (YYYY-MM-DD) — the canonical "when did this
      // job actually happen" reference. Used for the Invoices page's
      // date column, cycle bucketing, and monthly bill generation.
      // More reliable than completedAt (which depends on guard
      // marking the job complete) or createdAt (when the invoice was
      // generated, not when the job ran).
      scheduledDate: job.scheduledDate || "",
      scheduledTime: job.scheduledTime || "",
      // Bundle info — copied so the Invoices page can group + email DAI
      // submissions without round-tripping to load the source jobs.
      bundleId: job.bundleId || null,
      salesRepName: job.salesRepName || "",
      salesRepPhone: job.salesRepPhone || "",
      guardNumber: n,
      guardName: a?.guardName || "",
      guardId: a?.guardId || null,
      isDriver,
      jobType: job.jobType || job.type || "",
      airportCode: job.airportCode || "",
      routeSummary,
      // Package numbers + addresses, for the Statements detail rows.
      packageDetails: packageDetailsFor(job),
      // A real date only. job.completedAt can still be the unresolved
      // serverTimestamp() placeholder from a local update, which was being
      // saved as {_methodName:"serverTimestamp"} and left the invoice with
      // no usable completion date. (The pay cycle above is unchanged.)
      completedAt: tsToDate(completedAt)
        ? completedAt
        : (tsToDate(job.completedAtMs)?.getTime() ?? Date.now()),
      // Flat-rate jobs record 0 hours/miles — the fee is fixed, not
      // computed from time/distance. Otherwise stamp THIS guard's
      // hours (may differ from job-level when the guard split off
      // early — see hoursForGuard above).
      hoursCharged: useFlat ? 0 : thisGuardHours,
      miles: useFlat ? 0 : guardMilesN,
      mileageSets, // recorded for the audit trail
      expenses: guardExpenses,
      hourlyRate: useFlat ? 0 : hourlyRate,
      mileageRate: useFlat ? 0 : mileageRate,
      flatFee: useFlat ? flatFeePerGuard : 0,
      total,
      // Independent billing/pay switches (used by cancelled-trip jobs). Default
      // true so every normal job bills the broker AND pays the guard as before.
      //   billBroker=false → excluded from broker statements (not charged)
      //   payGuard=false   → excluded from the guard's earnings
      billBroker: job.billBroker !== false,
      payGuard: job.payGuard !== false,
      // Approving IS sending here: the owner downloads the package and emails
      // it to the broker, then approves. Created as "draft" these sat unsent
      // forever — hasSentInvoicesForJob said nothing had gone out, statements
      // showed a growing pile of drafts, and the double-bill guard never
      // armed. Stamped sent so the record matches what the broker holds.
      status: "sent",
      sentAt: serverTimestamp(),
      payCycle: cycle,
      createdAt: serverTimestamp(),
    };

    // eslint-disable-next-line no-await-in-loop
    const ref = await addDoc(collection(db, "invoices"), docData);
    created.push({ id: ref.id, invoiceNumber, total });
  }

  return created;
}

// ── Read / watch / update ────────────────────────────────────────────

export function watchInvoices(companyId, callback) {
  const q = query(
    collection(db, "invoices"),
    where("companyId", "==", companyId),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// Watch all invoices belonging to a single guard. Used by the owner's
// per-guard summary view and the guard's own Earnings tab.
export function watchInvoicesForGuard(companyId, guardId, callback) {
  if (!guardId) return () => {};
  const q = query(
    collection(db, "invoices"),
    where("companyId", "==", companyId),
    where("guardId", "==", guardId),
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

export async function getInvoice(invoiceId) {
  const snap = await getDoc(doc(db, "invoices", invoiceId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function setInvoiceStatus(invoiceId, status) {
  if (!["draft", "sent", "paid", "void"].includes(status)) {
    throw new Error(`Bad invoice status: ${status}`);
  }
  const stampField =
    status === "sent" ? "sentAt" :
    status === "paid" ? "paidAt" :
    status === "void" ? "voidedAt" : null;
  const patch = { status };
  if (stampField) patch[stampField] = serverTimestamp();
  // Read first so we know which job this invoice belongs to — needed
  // for the auto-unlock side-effect below.
  const invRef = doc(db, "invoices", invoiceId);
  const invSnap = await getDoc(invRef);
  await updateDoc(invRef, patch);

  // ── Auto-unlock side effect ─────────────────────────────────────
  // When voiding an invoice, check whether the underlying job still
  // has any non-void invoices left. If not, reset the job's
  // invoiceStatus back to "unpaid" so Ready-to-Bill can re-approve it.
  // Without this, the job stays flagged as "invoiced" forever after
  // its only invoice gets voided, and re-approve trips the rule guard.
  if (status === "void" && invSnap.exists()) {
    try {
      const inv = invSnap.data();
      const jobId = inv.jobId;
      const companyId = inv.companyId;
      if (jobId && companyId) {
        const sibQ = query(
          collection(db, "invoices"),
          where("companyId", "==", companyId),
          where("jobId", "==", jobId),
        );
        const sibSnap = await getDocs(sibQ);
        const stillLive = sibSnap.docs.some((d) => {
          if (d.id === invoiceId) return false; // we just voided this one; the snapshot may not reflect yet
          return d.data().status !== "void";
        });
        if (!stillLive) {
          // Best-effort: revert the job to the pre-billing state so
          // Ready-to-Bill picks it up again. Wrapped in try/catch so a
          // rule denial doesn't break the void itself.
          await updateDoc(doc(db, "jobs", jobId), {
            invoiceStatus: "unpaid",
            billingApprovedAt: null,
          });
        }
      }
    } catch (err) {
      console.warn("Couldn't auto-unlock job after voiding its last invoice", err);
    }
  }
}

export async function setInvoicePayCycle(invoiceId, cycle) {
  return updateDoc(doc(db, "invoices", invoiceId), { payCycle: cycle });
}

/** Generic patch — used by the Gmail-drafts flow to stamp gmailDraftId. */
export async function updateInvoice(invoiceId, patch) {
  return updateDoc(doc(db, "invoices", invoiceId), patch);
}

/**
 * Self-heal — for every invoice in the company missing scheduledDate
 * (older invoices created before we started saving it on the doc),
 * look up the underlying job and copy scheduledDate + dispatcherName
 * onto the invoice. Runs once when the Invoices page first loads;
 * subsequent loads are no-ops since every invoice will have it set.
 *
 * Returns the count of invoices backfilled — caller can flash a
 * toast if it's non-zero so the owner knows old data was repaired.
 */
export async function backfillInvoiceScheduledDates(companyId) {
  if (!companyId) return 0;
  let invSnap;
  try {
    invSnap = await getDocs(query(
      collection(db, "invoices"),
      where("companyId", "==", companyId),
    ));
  } catch (err) {
    console.warn("Backfill: couldn't read invoices", err);
    return 0;
  }
  // Find invoices missing scheduledDate. Skip already-healed ones to
  // make this idempotent.
  const needsHeal = invSnap.docs.filter((d) => !d.data().scheduledDate);
  if (needsHeal.length === 0) return 0;
  // Cache job lookups so a job with N guard-invoices only triggers
  // one Firestore read. The cache stores null for missing/inaccessible
  // jobs so we don't keep retrying them on the same pass.
  const jobCache = new Map();
  let healed = 0;
  for (const docSnap of needsHeal) {
    const inv = docSnap.data();
    if (!inv.jobId) continue;
    let job = jobCache.get(inv.jobId);
    if (job === undefined) {
      // Wrap each job lookup individually so one orphaned or
      // permission-denied invoice doesn't abort the whole pass.
      try {
        // eslint-disable-next-line no-await-in-loop
        const jobSnap = await getDoc(doc(db, "jobs", inv.jobId));
        job = jobSnap.exists() ? jobSnap.data() : null;
      } catch (err) {
        console.warn(`Backfill: couldn't read job ${inv.jobId}`, err?.code || err?.message);
        job = null;
      }
      jobCache.set(inv.jobId, job);
    }
    if (!job) continue;
    const patch = {};
    if (job.scheduledDate) patch.scheduledDate = job.scheduledDate;
    if (job.scheduledTime) patch.scheduledTime = job.scheduledTime;
    if (!inv.dispatcherName && job.dispatcherName) {
      patch.dispatcherName = job.dispatcherName;
    }
    if (Object.keys(patch).length === 0) continue;
    try {
      // eslint-disable-next-line no-await-in-loop
      await updateDoc(docSnap.ref, patch);
      healed++;
    } catch (err) {
      console.warn(`Backfill: couldn't update invoice ${docSnap.id}`, err?.code || err?.message);
    }
  }
  return healed;
}

/**
 * Self-heal — re-stamp payCycle on invoices whose job completed on the
 * 1st of a month. The old payCycleFor bucketed day 1 into that month's
 * "A" half ("Aug 2 – 15") when the business cycle runs 16th–1st, so a
 * 1st-of-month completion belongs to the PREVIOUS month's "B" half
 * ("Jul 16 – Aug 1"). Recomputes with the fixed payCycleFor and patches
 * only when the stored value differs.
 *
 * Skips sent/paid/void invoices on purpose — those cycles were already
 * communicated on statements, and silently moving them would make our
 * records disagree with what the payer received. Idempotent; returns
 * the count fixed.
 */
export async function backfillPayCycleFirstOfMonth(companyId) {
  if (!companyId) return 0;
  let invSnap;
  try {
    invSnap = await getDocs(query(
      collection(db, "invoices"),
      where("companyId", "==", companyId),
    ));
  } catch (err) {
    console.warn("Pay-cycle backfill: couldn't read invoices", err);
    return 0;
  }
  const candidates = invSnap.docs.filter((d) => {
    const x = d.data();
    return x.payCycle && x.jobId && !["sent", "paid", "void"].includes(x.status);
  });
  if (candidates.length === 0) return 0;
  const jobCache = new Map();
  let fixed = 0;
  for (const docSnap of candidates) {
    const inv = docSnap.data();
    let job = jobCache.get(inv.jobId);
    if (job === undefined) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const jobSnap = await getDoc(doc(db, "jobs", inv.jobId));
        job = jobSnap.exists() ? jobSnap.data() : null;
      } catch (err) {
        console.warn(`Pay-cycle backfill: couldn't read job ${inv.jobId}`, err?.code || err?.message);
        job = null;
      }
      jobCache.set(inv.jobId, job);
    }
    if (!job) continue;
    const completed = tsToDate(job.completedAt || job.completedAtMs);
    if (!completed || completed.getDate() !== 1) continue;
    const expected = payCycleFor(completed);
    if (inv.payCycle === expected) continue;
    try {
      // eslint-disable-next-line no-await-in-loop
      await updateDoc(docSnap.ref, { payCycle: expected });
      fixed++;
    } catch (err) {
      console.warn(`Pay-cycle backfill: couldn't update invoice ${docSnap.id}`, err?.code || err?.message);
    }
  }
  return fixed;
}

/**
 * Self-heal — stamp packageDetails (Order/Receipt numbers + package
 * addresses) onto invoices created before we started denormalizing
 * them. Same pattern as backfillInvoiceScheduledDates: look up the
 * underlying job once per jobId, patch every invoice missing the
 * field, skip jobs we can't read. Idempotent — an invoice with
 * packageDetails set (even an empty-number row) is left alone.
 */
export async function backfillInvoicePackageDetails(companyId) {
  if (!companyId) return 0;
  let invSnap;
  try {
    invSnap = await getDocs(query(
      collection(db, "invoices"),
      where("companyId", "==", companyId),
    ));
  } catch (err) {
    console.warn("Package-details backfill: couldn't read invoices", err);
    return 0;
  }
  const needsHeal = invSnap.docs.filter((d) => !Array.isArray(d.data().packageDetails));
  if (needsHeal.length === 0) return 0;
  const jobCache = new Map();
  let healed = 0;
  for (const docSnap of needsHeal) {
    const inv = docSnap.data();
    if (!inv.jobId) continue;
    let job = jobCache.get(inv.jobId);
    if (job === undefined) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const jobSnap = await getDoc(doc(db, "jobs", inv.jobId));
        job = jobSnap.exists() ? jobSnap.data() : null;
      } catch (err) {
        console.warn(`Package-details backfill: couldn't read job ${inv.jobId}`, err?.code || err?.message);
        job = null;
      }
      jobCache.set(inv.jobId, job);
    }
    if (!job) continue;
    try {
      // eslint-disable-next-line no-await-in-loop
      await updateDoc(docSnap.ref, { packageDetails: packageDetailsFor(job) });
      healed++;
    } catch (err) {
      console.warn(`Package-details backfill: couldn't update invoice ${docSnap.id}`, err?.code || err?.message);
    }
  }
  return healed;
}

/**
 * One-time repair for flat-fee invoices that were written at $0 before
 * createInvoicesForJob became flat-fee aware. A DAI/flat-rate invoice
 * with total 0 whose underlying job carries a real billedAmount /
 * flatFeeOverride gets its total (and flatFee + routeSummary) corrected
 * in place. Skips paid invoices (a paid $0 is a reconciliation question,
 * not ours to silently rewrite) and void ones. Idempotent — once a
 * total is non-zero it's left alone. Returns the count fixed.
 */
export async function backfillFlatFeeInvoiceTotals(companyId) {
  if (!companyId) return 0;
  let invSnap;
  try {
    invSnap = await getDocs(query(
      collection(db, "invoices"),
      where("companyId", "==", companyId),
    ));
  } catch (err) {
    console.warn("Flat-fee backfill: couldn't read invoices", err);
    return 0;
  }
  // Candidates: $0 total, not paid, not void — the stale flat-fee bug.
  const candidates = invSnap.docs.filter((d) => {
    const x = d.data();
    return (Number(x.total) || 0) === 0 && x.status !== "paid" && x.status !== "void";
  });
  if (candidates.length === 0) return 0;

  const jobCache = new Map();
  let fixed = 0;
  for (const docSnap of candidates) {
    const inv = docSnap.data();
    if (!inv.jobId) continue;
    let job = jobCache.get(inv.jobId);
    if (job === undefined) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const jobSnap = await getDoc(doc(db, "jobs", inv.jobId));
        job = jobSnap.exists() ? jobSnap.data() : null;
      } catch (err) {
        console.warn(`Flat-fee backfill: couldn't read job ${inv.jobId}`, err?.code || err?.message);
        job = null;
      }
      jobCache.set(inv.jobId, job);
    }
    if (!job) continue;

    // Resolve the per-guard flat fee from the job. Prefer the explicit
    // override; fall back to splitting the job's billedAmount across
    // guards. If neither yields a positive number, this isn't a flat-
    // fee invoice we can repair — leave it.
    const numGuards = Math.max(1, Number(job.numGuards) || 1);
    let perGuard = Number(job.flatFeeOverride) || 0;
    if (!perGuard) {
      const billed = Number(job.billedAmount) || 0;
      if (billed > 0) perGuard = Math.round((billed / numGuards) * 100) / 100;
    }
    if (!(perGuard > 0)) continue;

    // Route summary from the job's non-airport stops (matches the
    // denormalized field new invoices now carry).
    const stops = Array.isArray(job.stops) ? job.stops : [];
    const real = stops.filter((s) => s.type !== "airport");
    const routeSummary = real.length
      ? real.map((s) => (s.outOfTown ? "OUT OF TOWN" : (s.locationName || "—"))).join(" → ")
      : "";

    const patch = {
      total: perGuard,
      flatFee: perGuard,
      hoursCharged: 0,
      miles: 0,
      hourlyRate: 0,
      mileageRate: 0,
    };
    if (routeSummary && !inv.routeSummary) patch.routeSummary = routeSummary;

    try {
      // eslint-disable-next-line no-await-in-loop
      await updateDoc(docSnap.ref, patch);
      fixed++;
    } catch (err) {
      console.warn(`Flat-fee backfill: couldn't update invoice ${docSnap.id}`, err?.code || err?.message);
    }
  }
  return fixed;
}

/**
 * Record receipt of a payment (one check / wire / Zelle batch) covering
 * a list of invoices. Marks every invoice paid and stamps the same
 * paymentBatchId on each so we can later reconstruct "which invoices
 * were on check #12345."
 *
 * Args:
 *   invoiceIds — array of invoice doc ids being paid.
 *   info       — { paymentReference, paymentReceivedAt, paymentAmount? }
 *
 * Returns the generated batch id so the caller can show a confirmation.
 */
export async function markInvoicesPaid(invoiceIds, info = {}) {
  if (!Array.isArray(invoiceIds) || invoiceIds.length === 0) return null;
  const batchId = `pay_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const receivedAtISO = info.paymentReceivedAt
    ? new Date(info.paymentReceivedAt).toISOString()
    : new Date().toISOString();
  const patch = {
    status: "paid",
    paidAt: serverTimestamp(),
    paymentBatchId: batchId,
    paymentReference: info.paymentReference || "",
    paymentReceivedAt: receivedAtISO,
  };
  if (info.paymentAmount != null) {
    patch.paymentBatchAmount = Number(info.paymentAmount) || 0;
  }
  // Sequential — keeps Firestore happy on large batches and lets the
  // caller see each write succeed (or fail) discretely.
  for (const id of invoiceIds) {
    // eslint-disable-next-line no-await-in-loop
    await updateDoc(doc(db, "invoices", id), patch);
  }
  return batchId;
}

/**
 * Record what the courier owner paid the guard for this invoice. Independent
 * of broker payment status — owner may pay the guard before the broker pays
 * (advance), at the same time, or after.
 */
export async function setGuardPaid(invoiceId, amount, note = "") {
  const num = Number(amount);
  if (!Number.isFinite(num) || num < 0) throw new Error("Invalid amount");
  return updateDoc(doc(db, "invoices", invoiceId), {
    guardPaidAmount: num,
    guardPaidAt: serverTimestamp(),
    guardPayNote: note || "",
  });
}

/**
 * Record one check that covers several of a guard's jobs. Each invoice gets
 * its share as guardPaidAmount (so money out counts in each JOB's month),
 * the check's date as guardPaidAt, and the check # + date as the note. All
 * written together — a check is never half-recorded.
 *
 * @param lines      [{ invoiceId, amount }] — amounts in dollars, > 0
 * @param paidDate   "YYYY-MM-DD" from the check stub
 * @param checkNumber optional
 */
export async function recordGuardCheck(lines, paidDate, checkNumber = "") {
  const [y, m, d] = String(paidDate || "").split("-").map(Number);
  if (!y || !m || !d) throw new Error("Enter the date on the check");
  if (!lines.length) throw new Error("Tick at least one job");
  const paidAt = Timestamp.fromDate(new Date(y, m - 1, d, 12));
  const note = `Check${checkNumber ? ` #${checkNumber}` : ""} · ${m}/${d}/${String(y).slice(2)}`;
  const batch = writeBatch(db);
  for (const { invoiceId, amount } of lines) {
    const num = Math.round(Number(amount) * 100) / 100;
    if (!Number.isFinite(num) || num <= 0) throw new Error("Every ticked job needs an amount");
    batch.update(doc(db, "invoices", invoiceId), {
      guardPaidAmount: num,
      guardPaidAt: paidAt,
      guardPayNote: note,
      guardCheck: { number: checkNumber || "", date: paidDate },
    });
  }
  await batch.commit();
}

/** Clear guard-paid status (mistake correction). */
export async function clearGuardPaid(invoiceId) {
  return updateDoc(doc(db, "invoices", invoiceId), {
    guardPaidAmount: null,
    guardPaidAt: null,
    guardPayNote: "",
    guardCheck: null,
  });
}

/**
 * Mark a set of bundled draft invoices as Sent in one shot. Used by the
 * Invoices page "Bundle & email" action so DAI submission flips them all
 * together without N round-trips.
 */
export async function markInvoicesSent(invoiceIds) {
  await Promise.all(
    (invoiceIds || []).map((id) =>
      updateDoc(doc(db, "invoices", id), {
        status: "sent",
        sentAt: serverTimestamp(),
      })
    )
  );
}
